import { onBeforeUnmount } from 'vue';

// Wires the device/browser BACK button to dismiss an open overlay instead of
// navigating away from the page underneath. The "back dismisses the overlay"
// pattern: opening pushes a throwaway history entry, so pressing back pops that
// entry and we close the overlay (staying on the current page). Closing via the
// UI (X / Escape) calls history.back() to consume the pushed entry, keeping
// history clean so the user never has to press back twice.
//
// Pass the overlay's close callback (usually `() => emits('close')`). Call
// open() when the overlay becomes visible and close() when it is dismissed
// through the UI; both are idempotent and safe to call when nothing was opened.
export function useBackToClose(onClose) {
    let pushed = false;
    let closingViaPopstate = false;

    function onPopstate() {
        if (!pushed) {
            return;
        }

        // The browser already popped our entry; mirror that into component state.
        // close() then sees closingViaPopstate and skips a second history.back().
        closingViaPopstate = true;
        onClose();
    }

    function open() {
        if (pushed) {
            return;
        }

        pushed = true;
        closingViaPopstate = false;
        window.history.pushState({ lhGalleryOpen: true }, '');
        window.addEventListener('popstate', onPopstate);
    }

    function close() {
        // Remove the listener before history.back() so the synthetic popstate it
        // fires doesn't re-enter onPopstate.
        window.removeEventListener('popstate', onPopstate);

        // Only unwind history for UI-driven closes; a back-button close was
        // already popped by the browser, so re-popping would skip a page.
        if (pushed && !closingViaPopstate) {
            window.history.back();
        }

        pushed = false;
        closingViaPopstate = false;
    }

    onBeforeUnmount(() => {
        window.removeEventListener('popstate', onPopstate);
    });

    return { open, close };
}
